/**
 * Treatment Accordion Module
 * 施術ボタンをクリックすると該当行の直下にアコーディオンを表示
 */
export class TreatmentAccordion {
  constructor() {
    this.root = document.querySelector('.l-treatment');
    this.eventCleanups = [];
    this.onResize = null;
    this.resizeFrame = null;

    if (!this.root) {
      return;
    }

    this.list = this.root.querySelector('.l-treatment__list');
    this.items = Array.from(this.root.querySelectorAll('.l-treatment__item'));
    this.panels = Array.from(this.root.querySelectorAll('.l-treatment__accordion'));
    this.detailButtons = Array.from(this.root.querySelectorAll('.l-treatment__detail-button'));
    this.detailBodies = Array.from(this.root.querySelectorAll('.l-treatment__detail-body'));
    this.itemTriggers = new Map(
      this.items
        .map((item) => [item, item.querySelector('.l-treatment__item-button')])
        .filter(([, trigger]) => trigger),
    );
    this.panelsByTreatment = new Map(
      this.panels.map((panel) => [panel.dataset.treatment, panel]),
    );
    this.currentItem = null;
    this.currentPanel = null;

    if (!this.list || this.items.length === 0 || this.panels.length === 0) {
      return;
    }

    this.bindEvents();
    this.syncDetailCounts();
    this.bindDetailTabs();
    this.initializeDetailTabs();
    this.bindDetailScroll();
    this.bindResize();
  }

  bindEvents() {
    this.itemTriggers.forEach((trigger, item) => {
      trigger.setAttribute('aria-expanded', 'false');
      const onClick = () => {
        this.toggle(item);
      };

      trigger.addEventListener('click', onClick);
      this.eventCleanups.push(() => {
        trigger.removeEventListener('click', onClick);
      });
    });
  }

  bindResize() {
    this.onResize = () => {
      this.updateDetailScrollState();

      if (!this.currentItem || !this.currentPanel) {
        return;
      }

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = window.requestAnimationFrame(() => {
          this.resizeFrame = null;
          if (!this.currentItem || !this.currentPanel) {
            return;
          }
          this.movePanelToRowEnd(this.currentItem, this.currentPanel);
          this.updateDetailScrollState();
        });
      });
    };
    window.addEventListener('resize', this.onResize);
  }

  destroy() {
    this.eventCleanups.forEach((cleanup) => cleanup());
    this.eventCleanups = [];

    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
      this.onResize = null;
    }

    if (this.resizeFrame) {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
    }
  }

  bindDetailScroll() {
    this.root.querySelectorAll('.l-treatment__detail-box').forEach((box) => {
      const onScroll = () => {
        this.updateDetailIndicator(box.closest('.l-treatment__detail-body'));
      };

      box.addEventListener('scroll', onScroll, { passive: true });
      this.eventCleanups.push(() => {
        box.removeEventListener('scroll', onScroll);
      });
    });
  }

  bindDetailTabs() {
    this.detailButtons.forEach((button) => {
      const onClick = () => {
        if (button.classList.contains('is-active')) {
          return;
        }

        this.setActiveDetailButton(button);
        this.showDetailBody(button.dataset.detailView);
      };

      button.addEventListener('click', onClick);
      this.eventCleanups.push(() => {
        button.removeEventListener('click', onClick);
      });
    });
  }

  initializeDetailTabs() {
    const activeButton =
      this.detailButtons.find((button) => button.classList.contains('is-active')) ||
      this.detailButtons[0];

    if (!activeButton) {
      return;
    }

    this.setActiveDetailButton(activeButton);
    this.showDetailBody(activeButton.dataset.detailView);
    this.updateDetailScrollState();
  }

  syncDetailCounts() {
    this.root.querySelectorAll('.l-treatment__detail-group').forEach((group) => {
      const count = group.querySelector('.l-treatment__detail-count');
      const items = group.querySelectorAll('.l-treatment__detail-item');

      if (!count) {
        return;
      }

      count.textContent = String(items.length);
    });
  }

  setActiveDetailButton(activeButton) {
    this.detailButtons.forEach((button) => {
      button.classList.toggle('is-active', button === activeButton);
    });
  }

  showDetailBody(view) {
    if (!view) {
      return;
    }

    this.detailBodies.forEach((body) => {
      const isTarget = body.dataset.detailView === view;
      body.hidden = !isTarget;

      if (!isTarget) {
        body.classList.remove('is-animating');
        return;
      }

      const box = body.querySelector('.l-treatment__detail-box');
      if (box) {
        box.scrollTop = 0;
      }

      body.classList.remove('is-animating');
      void body.offsetWidth;
      body.classList.add('is-animating');
    });

    window.requestAnimationFrame(() => {
      this.updateDetailScrollState();
    });
  }

  toggle(item) {
    if (this.currentItem === item) {
      this.close();
      return;
    }

    this.open(item);
  }

  open(item) {
    const panel = this.getPanel(item.dataset.treatment);
    if (!panel) {
      return;
    }

    this.setActiveItem(item);
    this.currentItem = item;
    this.currentPanel = panel;

    this.hidePanels();
    this.movePanelToRowEnd(item, panel);
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    panel.classList.add('is-open');
    this.resetDetailScrollPositions();

    window.requestAnimationFrame(() => {
      this.updateDetailScrollState();
    });
  }

  close() {
    this.setActiveItem(null);
    this.currentItem = null;
    this.currentPanel = null;
    this.hidePanels();
  }

  setActiveItem(activeItem) {
    this.items.forEach((item) => {
      const isActive = item === activeItem;
      item.classList.toggle('is-open', isActive);

      const trigger = this.itemTriggers.get(item);
      if (trigger) {
        trigger.setAttribute('aria-expanded', String(isActive));
      }
    });
  }

  hidePanels() {
    this.panels.forEach((panel) => {
      panel.classList.remove('is-open');
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    });
  }

  getPanel(treatment) {
    return this.panelsByTreatment.get(treatment) || null;
  }

  movePanelToRowEnd(item, panel) {
    const columns = this.getColumnCount();
    let rowItems = [];
    let currentRow = [];
    let occupiedColumns = 0;
    const captureRowIfTarget = () => {
      if (currentRow.includes(item)) {
        rowItems = currentRow;
      }
    };

    this.items.forEach((el) => {
      const span = el.classList.contains('is-wide') ? columns : 1;

      if (occupiedColumns + span > columns) {
        captureRowIfTarget();
        currentRow = [];
        occupiedColumns = 0;
      }

      currentRow.push(el);
      occupiedColumns += span;

      captureRowIfTarget();

      if (occupiedColumns >= columns) {
        captureRowIfTarget();
        currentRow = [];
        occupiedColumns = 0;
      }
    });

    captureRowIfTarget();

    const lastInRow = rowItems[rowItems.length - 1] || item;
    this.list.insertBefore(panel, lastInRow.nextSibling);
  }

  getColumnCount() {
    const value = getComputedStyle(this.list).getPropertyValue('--columns').trim();
    return Math.max(parseInt(value, 10) || 1, 1);
  }

  updateDetailScrollState() {
    this.detailBodies.forEach((body) => {
      const box = body.querySelector('.l-treatment__detail-box');
      const indicator = body.querySelector('.l-treatment__indicator');

      if (!box || !indicator || body.hidden) {
        return;
      }

      const shouldScroll = box.scrollHeight > box.clientHeight + 1;
      body.classList.toggle('is-scrollable', shouldScroll);
      indicator.style.display = shouldScroll ? '' : 'none';

      this.updateDetailIndicator(body);
    });
  }

  resetDetailScrollPositions() {
    this.detailBodies.forEach((body) => {
      const box = body.querySelector('.l-treatment__detail-box');

      if (box) {
        box.scrollTop = 0;
      }
    });
  }

  updateDetailIndicator(body) {
    if (!body || body.hidden) {
      return;
    }

    const box = body.querySelector('.l-treatment__detail-box');
    const indicator = body.querySelector('.l-treatment__indicator');
    const thumb = body.querySelector('.l-treatment__indicator-thumb');

    if (!box || !indicator || !thumb) {
      return;
    }

    const maxScroll = box.scrollHeight - box.clientHeight;
    const trackHeight = indicator.clientHeight;
    const thumbHeight = thumb.offsetHeight;
    const progress = maxScroll > 0 ? box.scrollTop / maxScroll : 0;
    const move = Math.max(0, (trackHeight - thumbHeight) * progress);

    thumb.style.top = `${move}px`;
  }
}
